import { CommonModule } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, MaybeAsync, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConversationApiService } from '@vendasta/conversation';
import { Widget } from '../webchat-form/widget';
import { KnowledgeSource } from '@vendasta/embeddings';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { catchError, firstValueFrom, map, shareReplay, tap, throwError } from 'rxjs';
import { InboxService } from '../../../../../core/src';
import { StickyFooterComponent } from '../../../components/sticky-footer/sticky-footer.component';
import { getBackUrl } from '../constants';
import { WebChatInstructionsComponent } from '../web-chat-instructions/web-chat-instructions.component';
import { WebchatFormComponent } from '../webchat-form/webchat-form.component';
import { AiKnowledgeService } from '@galaxy/ai-knowledge';

@Component({
  selector: 'inbox-edit-webchat',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    GalaxyPageModule,
    TranslateModule,
    MatIconModule,
    GalaxyAlertModule,
    WebChatInstructionsComponent,
    WebchatFormComponent,
    StickyFooterComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './edit-webchat.component.html',
  styleUrls: ['./edit-webchat.component.scss'],
})
export class EditWebchatComponent {
  @ViewChild('webchatFormComponent') webchatFormComponent: WebchatFormComponent;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly conversationApi = inject(ConversationApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly inboxService = inject(InboxService);
  private readonly knowledgeService = inject(AiKnowledgeService);

  private widgetFormData?: Widget;
  private customKnowledgeContent?: string;
  private knowledgeSources?: KnowledgeSource[];

  readonly isLoading = signal(true);
  readonly error = signal<null | string>(null);
  readonly backUrl = getBackUrl(this.inboxService.isBusinessApp, 3);

  webchat$ = this.conversationApi
    .getWidget({
      widgetId: this.route.snapshot.params.widgetId,
    })
    .pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      map((widget) => widget.widget),
      tap((widget) => (this.widgetFormData = widget)),
      catchError(async (err) => {
        this.error.set(err.message);
        return null;
      }),
      tap(() => this.isLoading.set(false)),
    );

  updateFormData(widget: Widget): void {
    this.widgetFormData = widget;
  }

  updateCustomKnowledgeFormData(content: string): void {
    this.customKnowledgeContent = content;
  }

  updateKnowledgeSources(sources: KnowledgeSource[]): void {
    this.knowledgeSources = sources;
  }

  async updateWebchat(): Promise<void> {
    this.isLoading.set(true);

    // widgetFormData is cleared when accessed for some reason...?
    const widgetFormData = this.widgetFormData;
    try {
      const widgetId = this.route.snapshot.params.widgetId;
      const configurationUrl = await this.inboxService.buildAiKnowledgeAppConfigurationUrl(widgetId);
      await this.knowledgeService.upsertKnowledgeForApp(
        this.inboxService.buildAppId(widgetId),
        'Web Chat: ' + widgetFormData?.name,
        configurationUrl,
        {
          businessProfile: widgetFormData.dataSources?.businessProfile,
          userInput: widgetFormData?.dataSources?.userInput,
          content: this.customKnowledgeContent,
        },
        this.knowledgeSources,
      );

      await firstValueFrom(
        this.conversationApi
          .updateWidget({
            ...widgetFormData,
            widgetId: widgetId,
            fieldMask: {
              paths: ['name', 'color', 'welcome_message', 'text_color', 'accent_color', 'accent_text_color'],
            },
          })
          .pipe(catchError((err) => throwError(() => err))),
      );

      this.webchatFormComponent.markAsSaved();
      this.snackbarService.openSuccessSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_UPDATED');
    } catch (e) {
      this.snackbarService.openErrorSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_UPDATE_ERROR');
    } finally {
      this.isLoading.set(false);
    }
  }

  back(): void {
    this.router.navigate([this.backUrl], { relativeTo: this.route });
  }

  canDeactivate(): MaybeAsync<boolean> {
    return this.webchatFormComponent.canDeactivate();
  }
}
